import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ATTACHMENT_HTML_SEARCH_INPUT_CLICK_EVENT,
  ATTACHMENT_IMAGE_AREA_CLICK_EVENT,
  HTMLDisplayProps,
  IMAGE_HIGHLIGHT_ERROR_EVENT,
  REPORT_CONTENT_HEIGHT_EVENT,
  UPDATE_HTML_HIGHLIGHT_SELECTION_EVENT,
  UPDATE_IMAGE_HIGHLIGHT_SELECTION_EVENT,
} from './attachment-list-types';
import {
  ATTACHMENT_HTML_SEARCH_INPUT_HIGHLIGHT_CLASS,
  ATTACHMENT_HTML_SEARCH_INPUT_UUID_DATA_ATTRIBUTE,
  ATTACHMENT_HTML_SELECTED_SEARCH_INPUT_CLASS,
  generateHTMLHighlightInjectionScript,
  generateImageHighlightInjectionScript,
  generateLinkInterceptScript,
  useHTMLHighlighElementCSSStyles,
} from './hooks';
import { captureException } from '@/utils/sentry';
import { StyleSheet } from 'react-native';
import { useTheme } from '@/contexts/theme-context';

export const AttachmentListHTMLDisplay: React.FC<HTMLDisplayProps> = ({
  htmlContent,
  maxHeight,
  imageHighlights,
  onAreaSelect,
  onHighlightElementSelect,
  selectedSearchInputUUID,
  showHighlights = true,
  width,
  zoomLevel = 1,
}) => {
  const [height, setHeight] = useState(100);

  const theme = useTheme();

  const highlightElementCSSStyles = useHTMLHighlighElementCSSStyles();

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const processedHtmlContent = useMemo(() => {
    const heightScript = `
    <script>
      function sendHeight() {
        window.parent.postMessage({
          data: { height: document.body.scrollHeight },
          type: '${REPORT_CONTENT_HEIGHT_EVENT}',
        }, '${window.location.origin}');
      }
      window.addEventListener('load', sendHeight);
      window.addEventListener('resize', sendHeight);
    </script>
    `;

    const viewportMeta = `<meta name="viewport" content="width=device-width, initial-scale=1.0" charset="UTF-8">`;

    const imageHighlightScript =
      imageHighlights && Object.keys(imageHighlights).length > 0
        ? `
          <script>
              ${generateImageHighlightInjectionScript({
                errorEvent: IMAGE_HIGHLIGHT_ERROR_EVENT,
                highlightColor: theme.colors.pepperGreen,
                imageAreaClickEvent: ATTACHMENT_IMAGE_AREA_CLICK_EVENT,
                imageHighlights: imageHighlights,
                selectedAreaID: selectedSearchInputUUID,
                selectedColor: theme.colors.positive,
                updateSelectionEvent: UPDATE_IMAGE_HIGHLIGHT_SELECTION_EVENT,
              })}

              if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', setupImageHighlights);
              } else {
                setupImageHighlights();
              }

              window.addEventListener('message', function(event) {
                if (event.origin !== '${window.location.origin}') return;

                try {
                  const message = event.data;
                  if (message.type === '${UPDATE_IMAGE_HIGHLIGHT_SELECTION_EVENT}') {
                    const selectedAreaID = message.data.selectedAreaID;
                    if (typeof window.updateImageHighlightSelection === 'function') {
                      window.updateImageHighlightSelection(selectedAreaID);
                    }
                  }
                } catch (e) {
                  if (window.parent !== window) {
                    window.parent.postMessage({
                      type: '${IMAGE_HIGHLIGHT_ERROR_EVENT}',
                      data: {
                        error: e instanceof Error ? e.message : String(e),
                        context: 'UPDATE_IMAGE_HIGHLIGHT_SELECTION_PARSE_ERROR'
                      }
                    }, '${window.location.origin}');
                  }
                }
              });
          </script>
        `
        : '';

    const htmlHighlightScript = `
      <script>
        ${generateHTMLHighlightInjectionScript({
          clickEvent: ATTACHMENT_HTML_SEARCH_INPUT_CLICK_EVENT,
          cssStyles: showHighlights ? highlightElementCSSStyles : '',
          highlightClass: ATTACHMENT_HTML_SEARCH_INPUT_HIGHLIGHT_CLASS,
          selectedClass: ATTACHMENT_HTML_SELECTED_SEARCH_INPUT_CLASS,
          updateSelectionEvent: UPDATE_HTML_HIGHLIGHT_SELECTION_EVENT,
          uuidDataAttribute: ATTACHMENT_HTML_SEARCH_INPUT_UUID_DATA_ATTRIBUTE,
        })}
      </script>
    `;

    const linkInterceptScript = `
      <script>
        ${generateLinkInterceptScript()}
      </script>
    `;

    // Attempt to inject the script to the head tag, with fallbacks if things don't exist
    if (htmlContent.includes('</head>')) {
      return htmlContent.replace(
        '</head>',
        `${viewportMeta}${heightScript}${imageHighlightScript}${htmlHighlightScript}${linkInterceptScript}</head>`,
      );
    }

    if (htmlContent.includes('<body')) {
      return htmlContent.replace(
        '<body',
        `<head>${viewportMeta}${heightScript}${imageHighlightScript}${htmlHighlightScript}${linkInterceptScript}</head><body`,
      );
    }

    return `<head>${viewportMeta}${heightScript}${imageHighlightScript}${htmlHighlightScript}${linkInterceptScript}</head>${htmlContent}`;
  }, [
    htmlContent,
    theme,
    imageHighlights,
    selectedSearchInputUUID,
    showHighlights,
    highlightElementCSSStyles,
  ]);

  useEffect(() => {
    if (
      !iframeRef.current ||
      !imageHighlights ||
      Object.keys(imageHighlights).length === 0
    ) {
      return;
    }

    const iframeWindow = iframeRef.current.contentWindow;
    if (iframeWindow) {
      iframeWindow.postMessage(
        {
          data: { selectedAreaID: selectedSearchInputUUID ?? null },
          type: UPDATE_IMAGE_HIGHLIGHT_SELECTION_EVENT,
        },
        window.location.origin,
      );
    }
  }, [selectedSearchInputUUID, imageHighlights]);

  useEffect(() => {
    if (!iframeRef.current) {
      return;
    }

    const iframeWindow = iframeRef.current.contentWindow;
    if (iframeWindow) {
      iframeWindow.postMessage(
        {
          data: { selectedSearchInputUUID: selectedSearchInputUUID ?? null },
          type: UPDATE_HTML_HIGHLIGHT_SELECTION_EVENT,
        },
        window.location.origin,
      );
    }
  }, [selectedSearchInputUUID]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      const messagePayload = event.data;
      if (
        messagePayload &&
        messagePayload.type === REPORT_CONTENT_HEIGHT_EVENT
      ) {
        const newHeight = maxHeight
          ? Math.min(messagePayload.data.height, maxHeight)
          : messagePayload.data.height;
        setHeight(newHeight);
      } else if (
        messagePayload &&
        messagePayload.type === ATTACHMENT_IMAGE_AREA_CLICK_EVENT
      ) {
        const { areaId, imageUrl } = messagePayload.data;
        if (areaId && onAreaSelect) {
          // Find the area in imageHighlights
          const areas = imageHighlights?.[imageUrl];
          const area = areas?.find((a) => a.id === areaId);
          if (area) {
            onAreaSelect(area);
          }
        }
      } else if (
        messagePayload &&
        messagePayload.type === IMAGE_HIGHLIGHT_ERROR_EVENT
      ) {
        const { error, context } = messagePayload.data;
        captureException(
          new Error(`Image Highlight Error [${context}]: ${error}`),
        );
      } else if (
        messagePayload &&
        messagePayload.type === ATTACHMENT_HTML_SEARCH_INPUT_CLICK_EVENT
      ) {
        const searchInputUUID = messagePayload.data.searchInputUUID;
        if (searchInputUUID) {
          onHighlightElementSelect?.(searchInputUUID);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [maxHeight, imageHighlights, onAreaSelect, onHighlightElementSelect]);

  return (
    <iframe
      allow=""
      data-testid="html-display-iframe"
      ref={iframeRef}
      referrerPolicy="no-referrer"
      sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox"
      srcDoc={processedHtmlContent}
      style={StyleSheet.flatten([
        styles.iframe,
        {
          height: height + 16,
          transform: `scale(${zoomLevel})`,
          width,
        },
      ])}
    />
  );
};

const styles = StyleSheet.create({
  iframe: {
    borderWidth: 0,
    transformOrigin: 'top left',
  },
});
